import { useState, useEffect } from 'react';
import { FiMenu, FiX, FiPhone, FiChevronDown } from 'react-icons/fi';

const DEFAULT_NAVBAR_DATA = {
  logo: "https://jgalfalah.com/backend/media/uploaded_images/uploaded_images/warm-wellness-center-logo-cs-2025-cs-removebg-preview.png",
  hotline: "+880 1818 773 300"
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [navbarData, setNavbarData] = useState(DEFAULT_NAVBAR_DATA);
  const [isLoading, setIsLoading] = useState(true);
  
  const colors = {
    primary: '#0760ad',
    secondary: '#098a48',
    tertiary: '#118a94',
    quaternary: '#339166',
    white: '#FFFFFF',
    lightBg: 'rgba(7, 96, 173, 0.05)'
  };

  const menuItems = [
    { name: 'Home', href: '/', submenu: null },
    { name: 'About Us', href: '/about', submenu: null },
    { name: 'Services', href: '/services' },
    { name: 'Our Doctors', href: '/doctors', submenu: null },
    { name: 'Health Packages', href: 'health-packages', submenu: null },
    { name: 'Online Report', href: 'online-report', submenu: null },

    { name: 'Contact Us', href: '/contact', submenu: null }
  ];

  useEffect(() => {
    const fetchNavbarData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/layout/navbar/`);
        if (response.ok) {
          const data = await response.json();
          setNavbarData(data);
        }
      } catch (error) {
        console.error('Error fetching navbar data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNavbarData();
  }, []);

  const toggleSubmenu = (index) => {
    setActiveSubmenu(activeSubmenu === index ? null : index);
  };

  if (isLoading) {
    return (
      <header className="bg-white shadow-sm sticky top-0 z-50 h-20">
        {/* Loading placeholder */}
      </header>
    );
  }

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      {/* Top Section with Logo and Contact */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-2">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={navbarData.logo || DEFAULT_NAVBAR_DATA.logo} 
              alt="Warm Wellness Center Logo"
              className="h-24 w-auto"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = DEFAULT_NAVBAR_DATA.logo;
              }}
            />
          </div>

          {/* Contact Info */}
          <div className="hidden md:flex items-center">
            <a 
              href={`tel:${navbarData.hotline || DEFAULT_NAVBAR_DATA.hotline}`} 
              className="flex items-center px-4 py-2 rounded-full transition-all"
              style={{ 
                color: colors.white,
                background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.tertiary} 100%)`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <FiPhone className="mr-2" /> 
              <span className="font-medium">Hotline: {navbarData.hotline || DEFAULT_NAVBAR_DATA.hotline}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden focus:outline-none"
            style={{ color: colors.primary }}
          >
            {isMenuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white py-4 border-t" style={{ borderColor: `${colors.primary}20` }}>
            <div className="flex flex-col space-y-3">
              {menuItems.map((item, index) => (
                <div key={index}>
                  <div 
                    className="flex justify-between items-center px-3 py-2 rounded-md"
                    style={{ 
                      backgroundColor: activeSubmenu === index ? `${colors.primary}10` : 'transparent',
                      color: colors.primary
                    }}
                  >
                    <a href={item.href} className="font-medium flex-grow">{item.name}</a>
                    {item.submenu && (
                      <button 
                        onClick={() => toggleSubmenu(index)}
                        className="p-1"
                      >
                        <FiChevronDown className={`transition-transform ${activeSubmenu === index ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  
                  {item.submenu && activeSubmenu === index && (
                    <div className="ml-6 mt-1 space-y-2 py-1">
                      {item.submenu.map((subItem, subIndex) => (
                        <a 
                          key={subIndex} 
                          href={subItem.href} 
                          className="block px-3 py-2 rounded-md font-medium"
                          style={{ 
                            color: colors.white,
                            backgroundColor: colors.primary
                          }}
                        >
                          {subItem.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              <div className="pt-4 mt-2 border-t" style={{ borderColor: `${colors.primary}20` }}>
                <a 
                  href={`tel:${navbarData.hotline || DEFAULT_NAVBAR_DATA.hotline}`} 
                  className="flex items-center px-3 py-2 rounded-md font-medium text-white"
                  style={{ 
                    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.tertiary} 100%)`
                  }}
                >
                  <FiPhone className="mr-2" /> Hotline: {navbarData.hotline || DEFAULT_NAVBAR_DATA.hotline}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Full-width Gradient Navigation */}
      <div 
        className="w-full"
        style={{
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.tertiary} 100%)`
        }}
      >
        <div className="container mx-auto px-4">
          <nav className="hidden md:flex justify-center space-x-1 py-3">
            {menuItems.map((item, index) => (
              <div 
                key={index} 
                className="relative group"
                onMouseEnter={() => item.submenu && setActiveSubmenu(index)}
                onMouseLeave={() => item.submenu && setActiveSubmenu(null)}
              >
                <a 
                  href={item.href} 
                  className={`flex items-center px-4 py-2 rounded-full font-medium transition-all ${activeSubmenu === index ? 'bg-white text-primary' : 'text-white hover:bg-white hover:bg-opacity-20'}`}
                >
                  {item.name}
                  {item.submenu && <FiChevronDown className="ml-1" />}
                </a>
                
                {item.submenu && activeSubmenu === index && (
                  <div 
                    className="absolute left-0 mt-1 w-56 rounded-lg shadow-lg py-1 z-10"
                    style={{ 
                      backgroundColor: colors.white,
                      border: `1px solid ${colors.primary}20`
                    }}
                  >
                    {item.submenu.map((subItem, subIndex) => (
                      <a 
                        key={subIndex} 
                        href={subItem.href} 
                        className="block px-4 py-2 font-medium transition-colors hover:bg-opacity-10 hover:bg-primary"
                        style={{ color: colors.primary }}
                      >
                        {subItem.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;